import * as _ from 'lodash';
import type { SelectOption, SetState } from '../../../../../../../types';
import FilterCardCheckboxes from '../../../../../../generic/Filter/FilterCardCheckboxes';
import FilterCardNestedCheckboxes from '../../../../../../generic/Filter/FilterCardNestedCheckboxes';
import { ReportOptionDetails } from '../../../../ReportGeneration/utils';
import { licenseIncludeOptions, reportTypeFormatMap } from '../../constants';
import type { ReportCategory } from '../../../../shared/types';
import type { FormatSelection } from '.';
import { isFreeTier } from '../../../../../../../../shared/subscriptions';
import { window } from '../../../../../../../globals';
import PaidFeatureGate from '../../../../../../generic/Gates/PaidFeatureGate';

type Props = {
  format: FormatSelection;
  category: ReportCategory;
  values: SelectOption[];
  setValues: SetState<SelectOption[]>;
  licenseValues: SelectOption[];
  setLicenseValues: SetState<SelectOption[]>;
};

const IncludeCheckboxGroup = ({
  format,
  category,
  values,
  setValues,
  licenseValues,
  setLicenseValues,
}: Props) => {
  const isPaidFeatureGateRestricted = isFreeTier(window?.user?.organization?.stripe_plan);
  const [licenseOptions, flatOptions] = _.partition(
    Object.values(ReportOptionDetails)
      .filter(({ format: applicableFormats, category: applicableCategories }) => {
        const isCorrectCategory = applicableCategories.includes(category);
        const isApplicableFormat = applicableFormats.includes(reportTypeFormatMap[format.value]);
        return isCorrectCategory && isApplicableFormat;
      })
      .map(({ key: value, title }) => {
        const isProjectDeclaredLicense = title === 'Project Declared License';
        const label = isProjectDeclaredLicense ? 'Declared Licenses' : title;
        const isNestedOption = licenseIncludeOptions.has(value) || isProjectDeclaredLicense;
        return { isNestedOption, label, value };
      }),
    ({ isNestedOption }) => isNestedOption,
  );

  return (
    <div className="width-full" data-test-id="sbom-filters-include">
      <FilterCardCheckboxes
        label="Included"
        options={flatOptions}
        values={values}
        onChange={setValues}
        renderOption={(option, checkbox) => {
          // Everything except the transitive dependencies option is a paid feature
          if (option.value === 'includeDeepDependencies') {
            return checkbox;
          }
          return (
            <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
              {checkbox}
            </PaidFeatureGate>
          );
        }}
      />
      <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
        <FilterCardNestedCheckboxes
          options={licenseOptions}
          values={licenseValues}
          onChange={setLicenseValues}
          parentCheckboxLabel="List of licenses" />
      </PaidFeatureGate>
    </div>);
};

export default IncludeCheckboxGroup;
